import { useState, useContext, useEffect } from 'react';
import CompanyTable from '../components/companies/CompanyTable';
import { Box, Container, Modal } from '@mui/material';
import { Select, Button, TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faDownload } from '@fortawesome/free-solid-svg-icons';
import { getCompanies, addCompany, editCompany, deleteCompany } from '../apis/axios';
import AuthContext from '../AuthContext';

function Companies() {
  const auth = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [tableInstance, setTableInstance] = useState(null);
  const[update,setUpdate]=useState(false);
  const handleUpdate=()=>{  
    setUpdate(!update);
  }
  // Fetch companies from the backend on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getCompanies();
       
        setCompanies(response.data);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, [update]);

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    manager_email: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  // Function to export data as CSV
  const exportToCSV = (data, filename) => {
    const headers = ['Company', 'Devices', 'Manager Email'];
    const rows = data.map(item => [
      item.name,
      item.device_count,
      item.manager_email,
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  //console.log(formData);

  // Handle form submission for adding a new company
  const handleAddCompany = async () => {
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.manager_email.trim();
    if (!trimmedName) {
      console.error('Validation failed: Company name is required');
      return;
    }

    const newCompany = { name: trimmedName,manager_email:trimmedEmail };
    try {
      const response = await addCompany(newCompany);
      handleUpdate();
      closeModal();
    } catch (error) {
      console.error('Error adding company:', error);
    }
  };

  // Handle form submission for updating an existing company
  const handleUpdateCompany = async () => {
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.manager_email.trim();
    if (!trimmedName) {
      console.error('Validation failed: Company name is required');
      return;
    }

    const updatedCompany = {
      id: Number(formData.id),
      name: trimmedName,
      manager_email: trimmedEmail,
    };
    try {
      const response = await editCompany(updatedCompany);
      const updatedCompanyData = {
        ...response.data,
      }
      handleUpdate();
      closeModal();
    } catch (error) {
      console.error('Error updating company:', error);
    }
  };

  // Handle edit company
  const handleEditCompany = (company) => {
    setFormData({
      id: company.id,
      name: company.name,
      manager_email: company.manager_email,
    });
    setIsEditing(true);
    setOpenModal(true);
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (!tableInstance) return;

    if (action === 'deleteAll') {
      try {
        await Promise.all(companies.map(company => deleteCompany(company.id)));
        setCompanies([]);
      } catch (error) {
        console.error('Error deleting all companies:', error);
      }
    } else if (action === 'deleteSelected') {
      const selectedRows = tableInstance.getSelectedRowModel().rows;
      const selectedIds = selectedRows.map(row => row.original.id);
      try {
        await Promise.all(selectedIds.map(id => deleteCompany(id)));
        setCompanies(prev => prev.filter(company => !selectedIds.includes(company.id)));
        tableInstance.resetRowSelection();
      } catch (error) {
        console.error('Error deleting selected companies:', error);
      }
    }
  };

  // Close modal and reset form
  const closeModal = () => {
    setOpenModal(false);
    setIsEditing(false);
    setFormData({
      id: null,
      name: '',
    });
  };

  // Ripple effect handler
  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add('export-button-ripple');

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  return (
    <Box display="flex" sx={{ maxWidth: 'none', justifyContent: 'center' }}>
      <Box flexGrow={1} ml={{ sm: 0 }} sx={{ maxWidth: 'none' }}>
        <Container
          className="py-4"
          sx={{
            maxWidth: 'none !important',
            overflowX: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <div style={{ margin: '0 auto', width: '75.5vw', maxWidth: '75.5vw' }}>
            {/* Header and Buttons */}
           <div className="md:flex-row lg:flex-row xl:flex-row flex justify-between items-center mb-4 flex-col ">
              <div className="flex items-center">
                <h1
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '32px',
                    fontWeight: 700,
                    color: '#202224',
                  }}
                >
                  Companies
                </h1>
              </div>
              <div className="flex flex-col md:flex-row xl:flex-row lg:flex-row gap-[15px] md:gap:5 align-middle">
               
                <Button
                  size="sm"
                  variant="filled"
                  className="export-button"
                  styles={{
                    root: {
                      width: '120px',
                      height: '36px',
                      backgroundColor: '#FF7A00',
                      borderRadius: '20px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba f(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                      '&:hover': {
                        backgroundColor: '#FF7A00',
                      },
                    },
                    label: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    },
                  }}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    exportToCSV(companies, 'companies.csv');
                  }}
                >
                  <FontAwesomeIcon
                    icon={faDownload}
                    style={{
                      height: '10px',
                      width: '10px',
                      color: '#fff',
                    }}
                  />
                  <span>Export</span>
                </Button>
                <Button
                  size="sm"
                  variant="filled"
                  className="export-button"
                  styles={{
                    root: {
                      width: '140px',
                      height: '36px',
                      backgroundColor: '#FF7A00',
                      borderRadius: '20px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                      '&:hover': {
                        backgroundColor: '#FF7A00',
                      },
                    },
                    label: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    },
                  }}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    setOpenModal(true);
                    setIsEditing(false);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    style={{
                      height: '10px',
                      width: '10px',
                      color: '#fff',
                    }}
                  />
                  <span>Add Companies</span>
                </Button>
              </div>
            </div>

            {/* Company Table */}
            <div>
              <CompanyTable
                data={companies}
                setCompanies={setCompanies}
                onEdit={handleEditCompany}
                setTableInstance={setTableInstance}
              />
            </div>
          </div>

          {/* Add/Edit Company Modal */}
          <Modal
            open={openModal}
            onClose={closeModal}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                backgroundColor: '#F7F7F7',
                padding: '20px 30px',
                borderRadius: '8px',
                width: '48.144vw',
                maxWidth: '48.144vw',
                position: 'relative',
                maxHeight: { xs: '90vh', sm: 'none' },
                overflowY: { xs: 'auto', sm: 'hidden' },
                boxShadow: '6px 6px 6px #00000040',
              }}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4">
                <h1
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '30px',
                    fontWeight: 700,
                    color: '#202224',
                  }}
                >
                  {isEditing ? 'Edit Company' : 'Add Company'}
                </h1>
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{
                    height: '17px',
                    width: '17px',
                    color: '#FF7A00',
                    cursor: 'pointer',
                  }}
                  onClick={closeModal}
                />
              </div>

              {/* Company Information Section */}
              <h2
                style={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: { xs: '18px', sm: '20px' },
                  fontWeight: 700,
                  color: '#202224',
                  marginBottom: '16px',
                }}
              >
                Company Information
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Company Name */}
                <div className="flex flex-col flex-1">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Company Name*
                  </label>
                  <TextInput
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Tech Cryptors Pvt Ltd"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </div>
                 <div className="flex flex-col flex-1 ">
                  <label
                    style={{
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '10px', sm: '12px' },
                      fontWeight: 600,
                      color: '#202224',
                      marginBottom: '6px',
                    }}
                  >
                    Manager Email*
                  </label>
                  <TextInput
                    value={formData.manager_email}
                    onChange={(e) => handleInputChange('manager_email', e.target.value)}
                    placeholder="e.g., manager@gmail.com"
                    styles={{
                      input: {
                        fontFamily: 'Nunito Sans, sans-serif',
                        fontSize: { xs: '10px', sm: '12px' },
                        color: '#595C60',
                        backgroundColor: '#FCFCFC',
                        border: '0.6px solid #595C60',
                        borderRadius: '6px',
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '200px' },
                        height: { xs: '36px', sm: '40px' },
                        '&:focus': {
                          borderColor: '#FF6B00',
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Add/Update Company Button */}
              <div className="flex justify-end">
                <Button
                  className="export-button"
                  styles={{
                    root: {
                      width: { xs: '100px', sm: '120px' },
                      height: { xs: '32px', sm: '36px' },
                      backgroundColor: '#FF7A00',
                      borderRadius: '20px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                      '&:hover': {
                        backgroundColor: '#FF7A00',
                      },
                    },
                    label: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    isEditing ? handleUpdateCompany() : handleAddCompany();
                  }}
                  disabled={!formData.name.trim()}
                >
                  {isEditing ? 'Update Company' : 'Add Company'}
                </Button>
              </div>
            </div>
          </Modal>
        </Container>
      </Box>

      {/* Ripple Effect Styles */}
      <style>
        {`
          .export-button {
            position: relative;
            overflow: hidden;
          }
          .export-button-ripple {
            position: absolute;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.4);
            transform: scale(0);
            animation: ripple 0.6s linear;
            pointer-events: none;
          }
          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
        `}
      </style>
    </Box>
  );
}

export default Companies;